import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Input,
  Alert,
  Spin,
  message,
  Tooltip,
  Tabs,
  Segmented,
  Collapse,
  Result,
  Modal,
  Form,
  Popconfirm,
  Descriptions,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  TrophyOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  DownloadOutlined,
  LinkOutlined,
  TableOutlined,
  BarsOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import { usePolicy } from "../../context/PolicyContext";
import { hasPolicyPermission } from "../../utils/policyUtils";
import "./IssuerDetails.css";

const { Title, Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const AGENCY_LABELS = {
  care: "CARE",
  crisil: "CRISIL",
  india: "India Ratings",
  icra: "ICRA",
  fitch: "Fitch",
  sp: "S&P",
};

const AGENCY_ORDER = ["care", "crisil", "india", "icra", "fitch", "sp"];

const getAgencyTitle = (agency) =>
  agency === "india"
    ? AGENCY_LABELS[agency]
    : `${AGENCY_LABELS[agency] || agency.toUpperCase()} Ratings`;

function IssuerRatings() {
  const { policies } = usePolicy();
  const canView = hasPolicyPermission(policies, "api:ratings:view");
  const canEdit = hasPolicyPermission(policies, "api:ratings:edit");
  const canDelete = hasPolicyPermission(policies, "api:ratings:delete");

  const location = useLocation();
  const navigate = useNavigate();
  const issuerName = location.state?.issuerName;
  const { isDark } = useContext(ThemeContext);

  const [ratingsData, setRatingsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState();
  const [viewMode, setViewMode] = useState("tab"); // "tab" or "accordion"
  const [noContent, setNoContent] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editForm] = Form.useForm();
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Use the new API endpoint
  const baseUrl =
    process.env.REACT_APP_BASE_URL || "http://localhost:8083/api/v1/sb";

  const fetchRatingsData = async (preserveCurrentTab = false) => {
    setLoading(true);
    setError(null);
    setNoContent(false);
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };
      const response = await apiClient.get(
        `${baseUrl}/ratings/issuer/${encodeURIComponent(issuerName)}`,
        { headers, validateStatus: () => true }
      );
      if (response.status === 204) {
        setNoContent(true);
        setRatingsData({});
        return;
      }
      if (response.data.statusCode === 200) {
        setRatingsData(response.data.data || {});
        
        if (preserveCurrentTab) {
          // Keep the current tab if it still has data, otherwise go to first available
          const availableAgencies = AGENCY_ORDER.filter(
            (agency) => response.data.data[agency] && response.data.data[agency].length > 0
          );
          if (availableAgencies.length > 0) {
            // Check if current tab still has data
            if (activeTab && response.data.data[activeTab] && response.data.data[activeTab].length > 0) {
              // Keep current tab
              setActiveTab(activeTab);
            } else {
              // Current tab has no data, go to first available
              setActiveTab(availableAgencies[0]);
            }
          }
        } else {
          // Initial load - Set first available tab as active from AGENCY_ORDER
          const availableAgencies = AGENCY_ORDER.filter(
            (agency) => response.data.data[agency] && response.data.data[agency].length > 0
          );
          if (availableAgencies.length > 0) {
            setActiveTab(availableAgencies[0]);
          }
        }
      } else {
        throw new Error(
          response.data.message || "Failed to fetch ratings data"
        );
      }
    } catch (error) {
      setError("Error fetching ratings data");
      message.error("Error fetching ratings data");
      console.error("Error fetching ratings data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle view rating details
  const handleView = (record, agency) => {
    setViewData({ ...record, agency });
    setViewModalVisible(true);
  };

  // Handle edit rating
  const handleEdit = (record, agency) => {
    setEditData({ ...record, agency });
    editForm.setFieldsValue({
      oldId: record.id,
      newId: {
        corporateIdentityNumber: record.id?.corporateIdentityNumber,
        date: record.id?.date,
        instrument: record.id?.instrument,
        rating: record.id?.rating,
        issuer: record.id?.issuer
      },
      ratingType: agency,
      ratingAmount: record.ratingAmount,
      outlook: record.outlook,
      outlookDate: record.outlookDate,
      ratingActionDescription: record.ratingActionDescription,
      ratingLocalValue: record.ratingLocalValue,
      rationaleTitle: record.rationaleTitle,
      rationaleUrl: record.rationaleUrl,
      downloadUrl: record.downloadUrl,
      reportUrl: record.reportUrl,
      amount: record.amount,
      currency: record.currency,
      pressReleaseUrl: record.pressReleaseUrl,
      pressReleaseTitle: record.pressReleaseTitle,
      lastUpdated: record.lastUpdated,
      rationaleDate: record.rationaleDate,
      rationaleLink: record.rationaleLink,
    });
    setEditModalVisible(true);
  };

  // Handle delete rating
  const handleDelete = async (record, agency) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };
      const deleteRequest = {
        ratingsId: record.id,
        ratingType: agency
      };

      const response = await apiClient.delete(
        `${baseUrl}/ratings/delete`,
        {
          headers,
          data: deleteRequest,
          validateStatus: () => true
        }
      );

      if (response.data.statusCode === 200) {
        message.success(`${agency.toUpperCase()} rating deleted successfully`);
        fetchRatingsData(true); // Refresh data and preserve current tab
      } else {
        throw new Error(response.data.message || "Failed to delete rating");
      }
    } catch (error) {
      message.error("Error deleting rating: " + error.message);
      console.error("Error deleting rating:", error);
    }
  };

  // Handle update rating
  const handleUpdate = async (values) => {
    setEditLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };
      const response = await apiClient.put(
        `${baseUrl}/ratings/update`,
        values,
        { headers, validateStatus: () => true }
      );

      if (response.data.statusCode === 200) {
        message.success(`${values.ratingType.toUpperCase()} rating updated successfully`);
        setEditModalVisible(false);
        fetchRatingsData(true); // Refresh data and preserve current tab
      } else {
        throw new Error(response.data.message || "Failed to update rating");
      }
    } catch (error) {
      message.error("Error updating rating: " + error.message);
      console.error("Error updating rating:", error);
    } finally {
      setEditLoading(false);
    }
  };

  useEffect(() => {
    if (issuerName) {
      fetchRatingsData();
    }
    // eslint-disable-next-line
  }, [issuerName]);

  const handleRefresh = () => {
    fetchRatingsData();
  };

  // Back button handler
  const handleGoBack = () => {
    navigate(-1);
  };

  // Agency-specific columns
  const getColumns = (agency) => {
    const baseColumns = [
      {
        title: "Date",
        dataIndex: ["id", "date"],
        key: "date",
        render: (text, record) => record.id?.date || "-",
      },
      {
        title: "Instrument",
        dataIndex: ["id", "instrument"],
        key: "instrument",
        render: (text, record) => record.id?.instrument || "-",
      },
      {
        title: "Rating",
        dataIndex: ["id", "rating"],
        key: "rating",
        render: (text, record) => {
          const colors = {
            fitch: "blue",
            icra: "purple",
            crisil: "green",
            india: "orange",
            care: "magenta",
            sp: "cyan"
          };
          return <Tag color={colors[agency] || "default"}>{record.id?.rating || "-"}</Tag>;
        },
      }
    ];

    // Add agency-specific columns
    let agencyColumns = [];
    switch (agency) {
      case "fitch":
        agencyColumns = [
          {
            title: "Action",
            dataIndex: "ratingActionDescription",
            key: "ratingActionDescription",
            render: (text) => text || "-",
          },
          {
            title: "Rationale",
            dataIndex: "rationaleTitle",
            key: "rationaleTitle",
            render: (text, record) =>
              record.rationaleUrl ? (
                <Tooltip title={text}>
                  <a
                    href={record.rationaleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {text || "View"} <LinkOutlined />
                  </a>
                </Tooltip>
              ) : (
                text || "-"
              ),
          },
        ];
        break;
      case "icra":
        agencyColumns = [
          {
            title: "Rationale",
            dataIndex: "rationaleTitle",
            key: "rationaleTitle",
            render: (text, record) =>
              record.reportUrl ? (
                <Tooltip title={text}>
                  <a
                    href={record.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {text || "View"} <LinkOutlined />
                  </a>
                </Tooltip>
              ) : (
                text || "-"
              ),
          },
          {
            title: "Download",
            dataIndex: "downloadUrl",
            key: "downloadUrl",
            render: (text) =>
              text ? (
                <a href={text} target="_blank" rel="noopener noreferrer">
                  <Button icon={<DownloadOutlined />} size="small">
                    PDF
                  </Button>
                </a>
              ) : (
                "-"
              ),
          },
        ];
        break;
      case "crisil":
        agencyColumns = [
          {
            title: "Outlook",
            dataIndex: "outlook",
            key: "outlook",
            render: (text) => text || "-",
          },
        ];
        break;
      case "india":
        agencyColumns = [
          {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            render: (text, record) =>
              text ? `${text} ${record.currency || ""}` : "-",
          },
          {
            title: "Press Release",
            dataIndex: "pressReleaseTitle",
            key: "pressReleaseTitle",
            render: (text, record) =>
              record.pressReleaseUrl ? (
                <Tooltip title={text}>
                  <a
                    href={record.pressReleaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {text || "View"} <LinkOutlined />
                  </a>
                </Tooltip>
              ) : (
                text || "-"
              ),
          },
        ];
        break;
      case "care":
        agencyColumns = [
          {
            title: "Amount",
            dataIndex: "ratingAmount",
            key: "ratingAmount",
            render: (text) => text || "-",
          },
          {
            title: "File",
            dataIndex: "fileTitle",
            key: "fileTitle",
            render: (text, record) =>
              record.fileUrl ? (
                <Tooltip title={text}>
                  <a
                    href={record.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {text || "View"} <LinkOutlined />
                  </a>
                </Tooltip>
              ) : (
                text || "-"
              ),
          },
        ];
        break;
      case "sp":
        agencyColumns = [
          {
            title: "Last Updated",
            dataIndex: "lastUpdated",
            key: "lastUpdated",
            render: (text) => text || "-",
          },
          {
            title: "Rationale",
            dataIndex: "rationaleLink",
            key: "rationaleLink",
            render: (text) =>
              text ? (
                <a href={text} target="_blank" rel="noopener noreferrer">
                  View <LinkOutlined />
                </a>
              ) : (
                "-"
              ),
          },
        ];
        break;
    }

    // Add actions column
    const actionsColumn = (canView || canEdit || canDelete) ? [
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <span style={{ display: "flex", gap: 8 }}>
            {/* {canView && ( */}
            <Tooltip title="View Details">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleView(record, agency)}
              />
            </Tooltip>
            {/* )} */}
            {canEdit && (
              <Tooltip title="Edit">
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record, agency)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <Popconfirm
                  title={`Are you sure you want to delete this ${agency.toUpperCase()} rating?`}
                  onConfirm={() => handleDelete(record, agency)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="link" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
          </span>
        ),
      }
    ] : [];

    return [...baseColumns, ...agencyColumns, ...actionsColumn];
  };

  // Filtered data for search
  const getFilteredData = (agency) => {
    const data = ratingsData[agency] || [];
    if (!searchText) return data;
    return data.filter((item) => {
      const str = JSON.stringify(item).toLowerCase();
      return str.includes(searchText.toLowerCase());
    });
  };

  if (!issuerName) {
    return (
      <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
        <Result
          status="error"
          title="No issuer selected"
          subTitle="Please navigate to this page from an issuer dashboard."
        />
      </div>
    );
  }
  if (loading) {
    return (
      <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading ratings data...</Text>
        </div>
      </div>
    );
  }

  if (noContent) {
    return (
      <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
        <Result
          status="info"
          title="No ratings data available for this issuer."
          subTitle="There are currently no ratings records for this issuer from any agency."
        />
      </div>
    );
  }

  return (
    <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
      {/* Header Section */}
      <div className="issuer-details-header">
        <div className="issuer-details-header-content">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
            type="text"
            className="back-button"
          >
            Back
          </Button>
          <div className="issuer-details-title-section">
            <Title level={2} className="issuer-details-title">
              Credit Ratings - {issuerName}
            </Title>
            <div className="issuer-details-meta">
              <Tag color="blue" icon={<TrophyOutlined />}>
                Ratings by Agency
              </Tag>
              <Tag color="green">
                {Object.values(ratingsData).reduce(
                  (acc, arr) => acc + (arr?.length || 0),
                  0
                )}{" "}
                Total Ratings
              </Tag>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <Card className="controls-card">
        <div className="controls-header">
          <div className="controls-left">
            <Segmented
              options={[
                {
                  label: (
                    <span>
                      <TableOutlined /> Tab View
                    </span>
                  ),
                  value: "tab",
                },
                {
                  label: (
                    <span>
                      <BarsOutlined /> Accordion View
                    </span>
                  ),
                  value: "accordion",
                },
              ]}
              value={viewMode}
              onChange={setViewMode}
              style={{ marginRight: 16 }}
            />
            <Search
              placeholder="Search all ratings..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
          </div>
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Tabs for Agencies or Accordion View */}
      {viewMode === "tab" ? (
        <Card className="data-card">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            tabBarGutter={32}
          >
            {AGENCY_ORDER.filter(
              (agency) => ratingsData[agency] && ratingsData[agency].length > 0
            ).map((agency) => (
              <TabPane tab={getAgencyTitle(agency)} key={agency}>
                <Table
                  columns={getColumns(agency)}
                  dataSource={getFilteredData(agency)}
                  rowKey={(record, idx) => idx}
                  pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSize: 10,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  scroll={{ x: true }}
                  className={isDark ? "ant-table-dark" : ""}
                />
              </TabPane>
            ))}
          </Tabs>
        </Card>
      ) : (
        <Card className="data-card">
          <Collapse
            accordion
            bordered={false}
            expandIconPosition="end"
            className={isDark ? "ant-collapse-dark" : ""}
          >
            {AGENCY_ORDER.filter(
              (agency) => ratingsData[agency] && ratingsData[agency].length > 0
            ).map((agency) => (
              <Panel
                header={getAgencyTitle(agency)}
                key={agency}
                style={{ fontWeight: 600, fontSize: 18 }}
              >
                <Table
                  columns={getColumns(agency)}
                  dataSource={getFilteredData(agency)}
                  rowKey={(record, idx) => idx}
                  pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSize: 10,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  scroll={{ x: true }}
                  className={isDark ? "ant-table-dark" : ""}
                />
              </Panel>
            ))}
          </Collapse>
        </Card>
      )}

      {/* Edit Modal */}
      <Modal
        title={`Edit ${editData?.agency?.toUpperCase()} Rating`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item name="oldId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="newId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="ratingType" hidden>
            <Input />
          </Form.Item>

          {/* Issuer Name - Disabled */}
          <Form.Item label="Issuer Name">
            <Input
              value={editData?.id?.issuer || issuerName}
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          {/* Corporate Identity Number - Editable */}
          <Form.Item label="Corporate Identity Number" name={["newId", "corporateIdentityNumber"]}>
            <Input placeholder="Enter corporate identity number" />
          </Form.Item>

          {/* Date - Editable */}
          <Form.Item label="Date" name={["newId", "date"]}>
            <Input placeholder="Enter date" />
          </Form.Item>

          {/* Instrument - Editable */}
          <Form.Item label="Instrument" name={["newId", "instrument"]}>
            <Input placeholder="Enter instrument" />
          </Form.Item>

          {/* Rating - Editable */}
          <Form.Item label="Rating" name={["newId", "rating"]}>
            <Input placeholder="Enter rating" />
          </Form.Item>

          {/* Agency-specific editable fields */}
          {editData?.agency === 'care' && (
            <Form.Item label="Rating Amount" name="ratingAmount">
              <Input placeholder="Enter rating amount" />
            </Form.Item>
          )}

          {editData?.agency === 'crisil' && (
            <>
              <Form.Item label="Outlook" name="outlook">
                <Input placeholder="Enter outlook" />
              </Form.Item>
              <Form.Item label="Outlook Date" name="outlookDate">
                <Input placeholder="Enter outlook date" />
              </Form.Item>
            </>
          )}

          {editData?.agency === 'fitch' && (
            <>
              <Form.Item label="Rating Action Description" name="ratingActionDescription">
                <Input placeholder="Enter rating action description" />
              </Form.Item>
              <Form.Item label="Rating Local Value" name="ratingLocalValue">
                <Input placeholder="Enter rating local value" />
              </Form.Item>
              <Form.Item label="Rationale Title" name="rationaleTitle">
                <Input placeholder="Enter rationale title" />
              </Form.Item>
              <Form.Item label="Rationale URL" name="rationaleUrl">
                <Input placeholder="Enter rationale URL" />
              </Form.Item>
            </>
          )}

          {editData?.agency === 'icra' && (
            <>
              <Form.Item label="Download URL" name="downloadUrl">
                <Input placeholder="Enter download URL" />
              </Form.Item>
              <Form.Item label="Report URL" name="reportUrl">
                <Input placeholder="Enter report URL" />
              </Form.Item>
            </>
          )}

          {editData?.agency === 'india' && (
            <>
              <Form.Item label="Amount" name="amount">
                <Input placeholder="Enter amount" />
              </Form.Item>
              <Form.Item label="Currency" name="currency">
                <Input placeholder="Enter currency" />
              </Form.Item>
              <Form.Item label="Press Release URL" name="pressReleaseUrl">
                <Input placeholder="Enter press release URL" />
              </Form.Item>
              <Form.Item label="Press Release Title" name="pressReleaseTitle">
                <Input placeholder="Enter press release title" />
              </Form.Item>
            </>
          )}

          {editData?.agency === 'sp' && (
            <>
              <Form.Item label="Last Updated" name="lastUpdated">
                <Input placeholder="Enter last updated date" />
              </Form.Item>
              <Form.Item label="Rationale Date" name="rationaleDate">
                <Input placeholder="Enter rationale date" />
              </Form.Item>
              <Form.Item label="Rationale Link" name="rationaleLink">
                <Input placeholder="Enter rationale link" />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={editLoading}>
              Update Rating
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => setEditModalVisible(false)}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        title={`${viewData?.agency?.toUpperCase()} Rating Details`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {viewData && (
          <div>
            {/* Basic Information */}
            <Card title="Basic Information" style={{ marginBottom: 16 }}>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Issuer Name">
                  {viewData.id?.issuer || issuerName}
                </Descriptions.Item>
                <Descriptions.Item label="Corporate Identity Number">
                  {viewData.id?.corporateIdentityNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Date">
                  {viewData.id?.date}
                </Descriptions.Item>
                <Descriptions.Item label="Instrument">
                  {viewData.id?.instrument}
                </Descriptions.Item>
                <Descriptions.Item label="Rating">
                  <Tag color="blue">{viewData.id?.rating}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Rating Agency">
                  <Tag color="green">{viewData.agency?.toUpperCase()}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Agency-specific details */}
            {viewData.agency === 'care' && (
              <Card title="CARE Rating Details" style={{ marginBottom: 16 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Rating Amount">
                    {viewData.ratingAmount || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="File Title">
                    {viewData.fileTitle || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="File URL">
                    {viewData.fileUrl ? (
                      <a href={viewData.fileUrl} target="_blank" rel="noopener noreferrer">
                        View File <LinkOutlined />
                      </a>
                    ) : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created Date">
                    {viewData.createdDate || "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {viewData.agency === 'crisil' && (
              <Card title="CRISIL Rating Details" style={{ marginBottom: 16 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Outlook">
                    {viewData.outlook || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Outlook Date">
                    {viewData.outlookDate || "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {viewData.agency === 'fitch' && (
              <Card title="Fitch Rating Details" style={{ marginBottom: 16 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Rating Action Description">
                    {viewData.ratingActionDescription || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rating Local Value">
                    {viewData.ratingLocalValue || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rationale Title">
                    {viewData.rationaleTitle || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rationale URL">
                    {viewData.rationaleUrl ? (
                      <a href={viewData.rationaleUrl} target="_blank" rel="noopener noreferrer">
                        View Rationale <LinkOutlined />
                      </a>
                    ) : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {viewData.agency === 'icra' && (
              <Card title="ICRA Rating Details" style={{ marginBottom: 16 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Rationale Title">
                    {viewData.rationaleTitle || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Download URL">
                    {viewData.downloadUrl ? (
                      <a href={viewData.downloadUrl} target="_blank" rel="noopener noreferrer">
                        Download PDF <DownloadOutlined />
                      </a>
                    ) : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Report URL">
                    {viewData.reportUrl ? (
                      <a href={viewData.reportUrl} target="_blank" rel="noopener noreferrer">
                        View Report <LinkOutlined />
                      </a>
                    ) : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {viewData.agency === 'india' && (
              <Card title="India Rating Details" style={{ marginBottom: 16 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Amount">
                    {viewData.amount || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Currency">
                    {viewData.currency || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Outlook">
                    {viewData.outlook || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Press Release Title">
                    {viewData.pressReleaseTitle || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Press Release URL">
                    {viewData.pressReleaseUrl ? (
                      <a href={viewData.pressReleaseUrl} target="_blank" rel="noopener noreferrer">
                        View Press Release <LinkOutlined />
                      </a>
                    ) : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {viewData.agency === 'sp' && (
              <Card title="S&P Rating Details" style={{ marginBottom: 16 }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Last Updated">
                    {viewData.lastUpdated || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Outlook">
                    {viewData.outlook || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rationale Date">
                    {viewData.rationaleDate || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rationale Link">
                    {viewData.rationaleLink ? (
                      <a href={viewData.rationaleLink} target="_blank" rel="noopener noreferrer">
                        View Rationale <LinkOutlined />
                      </a>
                    ) : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default IssuerRatings;
